import axios from 'axios';
import { getSession } from 'next-auth/react';

import { isNewAuthProvider } from './newAuthProviderIds';

/**
 * Refresh the Curipod id_token for the current session and return the fresh token.
 *
 * New-auth (US_AUTH) sessions refresh through NextAuth itself: `getSession()` runs
 * the jwt callback — the single rotation owner — which rotates the single-use
 * refresh token AND, because this is a browser-originated `/api/auth/session`
 * request, persists the rotated token back to the session cookie. That cookie
 * write is the crucial part: the custom `/api/auth/refreshToken` endpoint cannot
 * update the browser cookie, so any rotation it performs is orphaned and the next
 * refresh fails. Routing every client refresh (REST 401s, game-socket
 * reconnects) through here makes NextAuth the sole rotator, so concurrent
 * refreshes across tabs reconcile on the backend's rotation grace window instead
 * of knocking each other out.
 *
 * Legacy B2C sessions are left byte-for-byte unchanged: they keep refreshing via
 * the `/api/auth/refreshToken` endpoint (validateSession + B2C token endpoint).
 *
 * @param provider the session provider (`session.provider`); decides the path.
 * @throws if the refresh failed (errored session / missing token / network).
 */
export async function refreshClientIdToken(
  provider: string | undefined,
): Promise<string> {
  if (isNewAuthProvider(provider)) {
    const session = await getSession();
    if (session?.error || !session?.idToken) {
      throw new Error((session?.error as string) ?? 'token refresh failed');
    }
    return session.idToken as string;
  }

  const res = await axios.get<{ id_token: string }>(
    `${window.location.origin}/api/auth/refreshToken`,
  );
  return res.data.id_token;
}
